import { memo, useMemo } from 'react';

import { ChevronDownIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, NavLink } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useDeskId } from '@/hooks/useDeskId';
import { useBroadcasts } from '@/modules/broadcast/hooks/useBroadcasts';
import { useHasDebts } from '@/modules/contacts/hooks/useDebts';
import { selectAccountSlug } from '@/redux/session/selectors';

import { navigationItems, type NavItem } from './helper';
import { matchesPath } from './utils';

interface ItemProps {
  item: NavItem;
  slug: string;
  isActive: boolean;
}

// Hover handles open/close for mouse users; only let keyboard activation through.
const handleTriggerClick = (e: React.MouseEvent<HTMLButtonElement>) => {
  if (e.detail > 0) {
    e.preventDefault();
  }
};

const BrandItem = memo(({ item, slug, isActive }: ItemProps) => {
  const { t } = useTranslation();
  return (
    <NavigationMenuItem>
      <InfoTooltip content={t('main_navigation.home')} delayDuration={200}>
        <span>
          <NavigationMenuLink className="px-3" asChild>
            <NavLink to={`/${slug}${item.link}`} data-active={isActive}>
              {item.icon}
            </NavLink>
          </NavigationMenuLink>
        </span>
      </InfoTooltip>
    </NavigationMenuItem>
  );
});
BrandItem.displayName = 'BrandItem';

const SubmenuItem = memo(({ item, slug, isActive }: ItemProps) => {
  const { t } = useTranslation();
  const subItems = item.submenuItems!;
  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger data-active={isActive} onClick={handleTriggerClick}>
        {item.icon}
        <span className="hidden lg:inline">{t(item.i18nKey!)}</span>
        <ChevronDownIcon size={12} />
      </NavigationMenuTrigger>
      <NavigationMenuContent className="-translate-y-1 p-1">
        <ul>
          {subItems.map((subItem) => (
            <li key={subItem.link}>
              <NavigationMenuLink
                className="w-full justify-start px-2 py-1.5 hover:bg-selected-soft focus:bg-selected-soft [&_svg]:opacity-100!"
                asChild
              >
                <NavLink
                  to={`/${slug}${subItem.link}`}
                  className="flex items-center gap-2 text-sm leading-5 font-normal"
                >
                  {subItem.icon}
                  {t(subItem.i18nKey)}
                </NavLink>
              </NavigationMenuLink>
            </li>
          ))}
        </ul>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
});
SubmenuItem.displayName = 'SubmenuItem';

const LinkItem = memo(({ item, slug, isActive }: ItemProps) => {
  const { t } = useTranslation();
  const showTooltip = useBreakpoint('lg');
  const label = t(item.i18nKey!);

  return (
    <NavigationMenuItem>
      <InfoTooltip content={showTooltip ? label : null} delayDuration={200}>
        <span>
          <NavigationMenuLink asChild>
            <NavLink to={`/${slug}${item.link}`} data-active={isActive}>
              {item.icon}
              <span className="hidden lg:inline">{label}</span>
            </NavLink>
          </NavigationMenuLink>
        </span>
      </InfoTooltip>
    </NavigationMenuItem>
  );
});
LinkItem.displayName = 'LinkItem';

export const MainList = () => {
  const deskId = useDeskId();
  const { showBroadcasts } = useBroadcasts();
  const showDebts = useHasDebts();
  const { pathname } = useLocation();
  const slug = useSelector(selectAccountSlug);

  const navItems = useMemo(
    () => navigationItems(deskId, showBroadcasts, showDebts),
    [deskId, showBroadcasts, showDebts]
  );

  return (
    <NavigationMenuList className="flex items-center">
      {navItems.map((item) => {
        const isSubMenuActive = !!item.submenuItems?.some(({ link }) =>
          matchesPath(pathname, link, slug)
        );
        const isMainItemActive = !!(item.link && matchesPath(pathname, item.link, slug));
        const isActive = isSubMenuActive || isMainItemActive;

        if (!item.i18nKey) {
          return <BrandItem key={item.link} item={item} slug={slug} isActive={isActive} />;
        }
        if (item.submenuItems) {
          return <SubmenuItem key={item.i18nKey} item={item} slug={slug} isActive={isActive} />;
        }
        return <LinkItem key={item.i18nKey} item={item} slug={slug} isActive={isActive} />;
      })}
    </NavigationMenuList>
  );
};
